import { createSelector } from 'reselect'
import { unique } from 'common/utils/arrayUtils'
import { DEFAULT_MODE_IDS } from 'tickets/utils/constants'
import { getCurrentModeId } from 'tickets/redux/modules/router/selectors'
import { getIsSelectingInbound } from 'tickets/redux/modules/selections/selectors'
import { getAvailableModeIds } from 'tickets/redux/modules/searchConfig/selectors'

const getModesInfo = state => state.modesInfo

const getActiveMode = createSelector(
  [getCurrentModeId, getModesInfo],
  (activeModeId, modesInfo) => modesInfo[activeModeId]
)

const getIsActiveModePolling = createSelector(
  [getActiveMode],
  (activeMode) => activeMode && activeMode.isPolling
)

const getHasActiveModeLoadedOutbound = createSelector(
  [getActiveMode],
  (activeMode) => !!(activeMode && activeMode.hasLoadedOutbound)
)

const getHasActiveModeLoadedInbound = createSelector(
  [getActiveMode],
  (activeMode) => !!(activeMode && activeMode.hasLoadedInbound)
)

const getHasActiveModeLoaded = createSelector(
  [getHasActiveModeLoadedOutbound, getHasActiveModeLoadedInbound],
  (hasLoadedOutbound, hasLoadedInbound) => hasLoadedOutbound && hasLoadedInbound
)

const getPage = createSelector(
  [getActiveMode],
  modeInfo => (modeInfo ? modeInfo.page : 0)
)

const getApiMessages = createSelector(
  [getActiveMode],
  (modeInfo) => (modeInfo ? modeInfo.messages : [])
)

const getErrorMessages = createSelector(
  [getActiveMode],
  (modeInfo) => (modeInfo ? modeInfo.errors : [])
)

const getIsTimeAware = createSelector(
  [getActiveMode],
  modeInfo => (modeInfo ? modeInfo.hourOfDayAware : false)
)

const getModeIdsToShow = createSelector(
  [getAvailableModeIds],
  (availableModeIds) => unique([...availableModeIds, ...DEFAULT_MODE_IDS])
)

const getLimitedResultsLoaded = createSelector(
  [getActiveMode],
  modeInfo => (modeInfo ? modeInfo.limitedResultsLoaded : false)
)

const getIsMoreFetchable = createSelector(
  [getActiveMode, getIsSelectingInbound],
  (activeModeInfo, isSelectingInbound) => {
    if (!activeModeInfo) {
      // Should this ever happen?
      return false
    }
    return (
      isSelectingInbound ?
        activeModeInfo.canFetchMoreInbound :
        activeModeInfo.canFetchMoreOutbound
    )
  }
)

const getHasResultsForTime = createSelector(
  [getActiveMode, getIsSelectingInbound],
  (activeModeInfo, isSelectingInbound) => {
    if (!activeModeInfo) {
      return false
    }

    return (
      isSelectingInbound ?
        !activeModeInfo.noResultsForTimeInbound :
        !activeModeInfo.noResultsForTimeOutbound
    )
  }
)

const getSupportsFerryFootPassenger = createSelector(
  [getActiveMode],
  modeInfo => (modeInfo ? !!modeInfo.supportsFerryFootPassenger : false)
)

const getSupportsFerryCarPassenger = createSelector(
  [getActiveMode],
  modeInfo => (modeInfo ? !!modeInfo.supportsFerryCarPassenger : false)
)

export {
  getModesInfo,
  getActiveMode,
  getHasActiveModeLoadedOutbound,
  getHasActiveModeLoadedInbound,
  getHasActiveModeLoaded,
  getPage,
  getApiMessages,
  getIsTimeAware,
  getModeIdsToShow,
  getIsMoreFetchable,
  getIsActiveModePolling,
  getHasResultsForTime,
  getSupportsFerryFootPassenger,
  getSupportsFerryCarPassenger,
  getLimitedResultsLoaded,
  getErrorMessages,
}
